import Component from 'vue-class-component';
import { Action, Getter, Mutation } from 'vuex-class';
import { StoreNamespace } from '@/globals/StoreNamespaceGlobal';
import BaseComponent from '@/components/base/BaseComponent';
import {
  EDIT_FORM_ERRORS,
  FORM_HEADERS,
  FORM_READ_ONLY_MODE,
  GET_FORM101_AS_PDF,
  GET_FORM101_DATA,
  GET_FORM_HEADERS,
  NO_PDF_ERROR,
  NO_PDF_ERRORS,
  SET_EDIT_FORM_ERRORS,
  SET_FORM101_ID,
  SET_FORM_HEADERS,
  SET_FORM_READ_ONLY_MODE,
  SET_FORM_REJECTED_REASON,
  SET_NO_PDF_ERROR,
  SET_NO_PDF_ERRORS,
  SET_OPEN_SAVE_BEFORE_LEAVE_WIZARD_MODAL,
  UPDATE_USER_OFFICE,
} from '@/types/form.types';
import { FormHeaderModel, FormHeaders } from '@/models/Form.model';
import { GridColumn } from '@/models/GridColumn.model';
import { ClickColumn } from '@/models/ClickColumn.model';
import { USER_DETAILS } from '@/types/authentication.types';
import { UserDetails } from '@/models/UserDetails.model';

@Component({})
export default class FormsComponent extends BaseComponent {

  @Action(GET_FORM_HEADERS, StoreNamespace.FORM) public getFormHeaders;
  @Action(GET_FORM101_DATA, StoreNamespace.FORM) public getFormData;
  @Action(GET_FORM101_AS_PDF, StoreNamespace.FORM) public getForm101AsPdf;
  @Action(UPDATE_USER_OFFICE, StoreNamespace.FORM) public updateUserOffice;

  @Mutation(SET_FORM_HEADERS, StoreNamespace.FORM) public setFormHeaders;
  @Mutation(SET_FORM101_ID, StoreNamespace.FORM) public setForm101Id;
  @Mutation(SET_EDIT_FORM_ERRORS, StoreNamespace.FORM) public setEditFormErrors;
  @Mutation(SET_FORM_READ_ONLY_MODE, StoreNamespace.FORM) public setFormReadOnlyMode;
  @Mutation(SET_NO_PDF_ERROR, StoreNamespace.FORM) public setNoPdfError;
  @Mutation(SET_NO_PDF_ERRORS, StoreNamespace.FORM) public setNoPdfErrors;
  @Mutation(SET_OPEN_SAVE_BEFORE_LEAVE_WIZARD_MODAL, StoreNamespace.FORM) public setOpenSaveBeforeLeaveWizardModal;
  @Mutation(SET_FORM_REJECTED_REASON, StoreNamespace.FORM) public setFormRejectedReason;

  @Getter(FORM_HEADERS, StoreNamespace.FORM) public formHeaders!: FormHeaders[];
  @Getter(EDIT_FORM_ERRORS, StoreNamespace.FORM) public editFormErrors!: string[];
  @Getter(FORM_READ_ONLY_MODE, StoreNamespace.FORM) public formReadOnlyMode!: boolean;
  @Getter(NO_PDF_ERROR, StoreNamespace.FORM) public noPdfError!: boolean;
  @Getter(NO_PDF_ERRORS, StoreNamespace.FORM) public noPdfErrors!: string[];
  @Getter(USER_DETAILS, StoreNamespace.AUTHENTICATION) public userDetails!: UserDetails;

  public employerDetails: any = {};

  private rejectMessage: string = '';
  private approveMessage: string = '';
  private openApproveFormDialog: boolean = false;
  private editableForm: FormHeaderModel = {} as FormHeaderModel;
  private columns: GridColumn[] = [];

  public mounted() {
    this.editableForm = {} as FormHeaderModel;
    this.setFormReadOnlyMode(false);
    this.setNoPdfError(false);
    this.setNoPdfErrors([]);
    this.getFormHeaders();
    this.initColumns();
  }

  public onAllFormsClick() {
    this.navigateTo('my-forms');
  }

  public navigateTo(route: string): void {
    this.$router.push({ name: route });
  }

  public onFormClick(form: any): void {
    this.getFormHeaders(form.formId);
  }

  public onTdClicked(item: any, row: any) {
    this.setOpenSaveBeforeLeaveWizardModal(false);
    this.updateUserOffice({ officeNum: item.officeNum, formId: item.formId })
      .then(() => {
        switch (row.clickEvent) {
          case 'DOWNLOAD_PDF':
            // this.getForm101AsPdfClicked(item);
            this.getForm101AsPdfClicked(item.formDocId);
            break;
          case 'VIEW':
            this.setFormReadOnlyMode(true);
            this.getFormData(item.formId);
            break;
          case 'EDIT':
            this.editableForm = item;
            if (item.status === 15) {
              this.rejectMessage = item.rejectMessage || '';
              this.setFormRejectedReason(this.rejectMessage);
              this.openApproveFormDialog = true;
            } else if (item.status === 20) {
              this.approveMessage = '' + this.$t('lobby.dialog.exist_and_approved_content');
              this.openApproveFormDialog = true;
            } else {
              this.getFormData(item.formId);
            }
        }
      });
  }

  private initColumns() {
    this.columns.push({
      property: 'taxYear', header: 'forms.year', hideSorting: true,
    } as GridColumn);
    this.columns.push({
      property: 'revision', header: 'forms.version', hideSorting: true,
    } as GridColumn);
    if (this.userDetails.offices && this.userDetails.offices.length > 1) {
      this.columns.push({
        property: 'officeNum', header: 'forms.officeName', hideSorting: true, subProperty: 'officeName',
      } as GridColumn);
    }
    this.columns.push({
      property: 'statusDesc', header: 'forms.status', hideSorting: true, classProperty: 'status',
    } as GridColumn);
    this.columns.push({
      property: 'updatedDate', header: 'forms.lastUpdated', hideSorting: true,
    } as GridColumn);
    this.columns.push({
      clickEvent: 'DOWNLOAD_PDF',
      faIconType: 'fal',
      faIcon: 'download',
      header: 'forms.pdf',
    } as ClickColumn);
    this.columns.push({
      clickEvent: 'VIEW',
      faIconType: 'fal',
      faIcon: 'eye',
      header: 'forms.view',
    } as ClickColumn);
    this.columns.push({
      clickEvent: 'EDIT',
      faIconType: 'fal',
      faIcon: 'pen',
      header: 'forms.edit',
    } as ClickColumn);
  }

  private getForm101AsPdfClicked(formDocId: number) {
    this.getForm101AsPdf(formDocId);
  }

  private closeEditFormErrorsDialog() {
    this.setEditFormErrors([]);
  }

  private closeDialog() {
    this.setNoPdfError(false);
    this.setNoPdfErrors([]);
    this.openApproveFormDialog = false;
    this.rejectMessage = '';
  }

  private confirmDialog() {
    this.getFormData(this.editableForm.formId);
  }

}

